import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Loader2, Mail, Send } from 'lucide-react';
import { apiClient } from '../../services/api/apiClient';
import { useLenis } from '../../hooks/useLenis';
import { useAuth } from '../../contexts/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';
import { SubscriptionCards } from '../../components/profile/SubscriptionCards';
import {
  getAvailablePlans,
  getSubscriptionStatus,
  getActiveDiscount,
  cancelSubscription,
  reactivateSubscription,
  cancelPendingDowngrade,
  type SubscriptionPlan,
  type SubscriptionStatusResponse,
  type ActiveDiscount,
} from '../../services/payment/subscriptionService';
import fcmsLimitsService, { type FcmsFeatureLimits } from '../../services/fcms/fcmsLimitsService';
import { invalidateFeatureAccessCache } from '../../hooks/useFeatureAccess';
import { PlanChangeDialog } from '../../components/profile/PlanChangeDialog';
import toast from '../../utils/toast';

/**
 * Pricing-/Paket-Auswahl-Seite.
 *
 * Doppelte Rolle:
 *  - Öffentlich erreichbar als `/pakete` für Gäste (Marketing-Einstieg in den
 *    Kombi-Checkout). Plan-Karten sind sichtbar, Klick startet Schritt A des
 *    Wizards via `/checkout?plan=...`.
 *  - Für eingeloggte User ohne aktives Pflicht-Abo (Bestands-User) als Gate
 *    via Redirect aus `ProtectedRoute`.
 *
 * Nutzt dieselbe `SubscriptionCards`-Komponente wie der Profil-Tab, damit
 * Plan-Karten, Pricing, Badges, Toggle (monatlich/jährlich) und Layout
 * konsistent bleiben.
 */
export const SubscriptionRequiredPageTW: React.FC = () => {
  useLenis();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, refreshSubscriptions, refreshUserProfile } = useAuth();
  const logoUrl = getImageUrl('/images/branding/renditefuchs-logo.svg');

  // Lokaler State analog zur ProfilePage -- selbe Datenquellen
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [activeDiscountData, setActiveDiscountData] = useState<ActiveDiscount | null>(null);
  const [fcmsLimits, setFcmsLimits] = useState<FcmsFeatureLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancellingSubscription, setCancellingSubscription] = useState<string | null>(null);
  const [reactivatingSubscription, setReactivatingSubscription] = useState<string | null>(null);

  // PlanChange-Dialog (z.B. Wechsel zwischen Bitcoin-Stacker ↔ Bitcoin-Whale)
  const [planChangeDialogOpen, setPlanChangeDialogOpen] = useState(false);
  const [planChangeTargetSlug, setPlanChangeTargetSlug] = useState<string>('');

  // Resend-Verification für Bestands-User mit email_verified=false
  const [resendingVerification, setResendingVerification] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      // getAvailablePlans + getActiveDiscount sind PUBLIC und funktionieren
      // auch ohne Login. getSubscriptionStatus erfordert Auth — nur für
      // eingeloggte User laden, sonst kommt 401 und wir verschwenden Roundtrip.
      const requests: Promise<any>[] = [getAvailablePlans(), getActiveDiscount()];
      if (isAuthenticated) {
        requests.push(getSubscriptionStatus());
      }
      const results = await Promise.all(requests);
      setPlans(results[0]);
      setActiveDiscountData(results[1]);
      if (isAuthenticated && results[2]) {
        setSubscriptionStatus(results[2]);
      }
      if (isAuthenticated) {
        try {
          const limits = await fcmsLimitsService.getLimits();
          setFcmsLimits(limits);
        } catch {
          setFcmsLimits(null);
        }
      }
    } catch (err) {
      console.error('Failed to load subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (isAuthenticated) {
      // Refresh AuthContext-State -- damit ProtectedRoute nach Webhook-getriebenem
      // Status-Update den User vom Pflicht-Gate freischaltet.
      refreshSubscriptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleSubscribe = (planSlug: string, interval: 'monthly' | 'yearly' = 'monthly') => {
    // Bestands-User mit unverifizierter Mail: erst zur Bestätigung zwingen.
    // Wir leiten ihn nicht zum Checkout, sondern zeigen einen klaren Toast und
    // scrollen den Banner sichtbar.
    if (isAuthenticated && user?.isEmailVerified === false) {
      toast.error('Bitte bestätige zuerst deine E-Mail-Adresse, bevor du ein Abo abschließt.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const params = new URLSearchParams({ plan: planSlug });
    if (interval === 'yearly') params.set('interval', 'yearly');
    navigate(`/checkout?${params.toString()}`);
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    setCancellingSubscription(subscriptionId);
    try {
      await cancelSubscription(subscriptionId);
      invalidateFeatureAccessCache();
      await Promise.all([fetchData(), refreshSubscriptions()]);
      toast.success('Abonnement wurde gekündigt.');
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Kündigen des Abonnements');
    } finally {
      setCancellingSubscription(null);
    }
  };

  const handleReactivateSubscription = async (subscriptionId: string) => {
    setReactivatingSubscription(subscriptionId);
    try {
      await reactivateSubscription(subscriptionId);
      invalidateFeatureAccessCache();
      await Promise.all([fetchData(), refreshSubscriptions()]);
      toast.success('Abonnement wurde reaktiviert.');
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Reaktivieren des Abonnements');
    } finally {
      setReactivatingSubscription(null);
    }
  };

  const handlePlanChange = (targetPlanSlug: string) => {
    setPlanChangeTargetSlug(targetPlanSlug);
    setPlanChangeDialogOpen(true);
  };

  const handlePlanChangeSuccess = async () => {
    setPlanChangeDialogOpen(false);
    invalidateFeatureAccessCache();
    await Promise.all([fetchData(), refreshSubscriptions()]);
  };

  const handleCancelPendingDowngrade = async () => {
    try {
      await cancelPendingDowngrade();
      invalidateFeatureAccessCache();
      await Promise.all([fetchData(), refreshSubscriptions()]);
      toast.success('Geplanter Plan-Wechsel wurde storniert.');
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Stornieren');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Bestands-User mit email_verified=false: erneute Verifizierungs-Mail anfordern.
  // Der Endpoint ist tolerant (akzeptiert auch unbekannte Mails), schickt aber
  // nur wirklich, wenn der User existiert und unverifiziert ist.
  const handleResendVerification = async () => {
    if (!user?.email || resendingVerification) return;
    setResendingVerification(true);
    try {
      const response = await apiClient.resendVerification(user.email);
      if (response.success) {
        toast.success('Bestätigungs-Mail wurde erneut versendet.');
      } else {
        const errMsg = response.error || 'Fehler beim Versand der Bestätigungs-Mail.';
        // Sonderfall: Backend meldet "schon bestätigt" — der lokale Auth-State
        // ist also veraltet. Wir holen das Profil neu, damit der Banner
        // verschwindet, und zeigen einen freundlichen Hinweis statt Fehler.
        if (/bereits best/i.test(errMsg)) {
          toast.success('Deine E-Mail ist bereits bestätigt — alles in Ordnung.');
          await refreshUserProfile();
        } else {
          toast.error(errMsg);
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Fehler beim Versand.');
    } finally {
      setResendingVerification(false);
    }
  };

  // Banner nur für eingeloggte Bestands-User mit unverifizierter E-Mail.
  // Bei Gästen ist isAuthenticated=false → Banner versteckt.
  const showVerificationBanner = isAuthenticated && user?.isEmailVerified === false;

  return (
    <div className="min-h-screen px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" aria-label="Zur Startseite" className="inline-block">
            <img alt="RENDITEFUCHS" src={logoUrl} className="mx-auto h-10 w-auto" />
          </Link>
          <h1 className="mt-8 text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            {isAuthenticated && user?.firstName
              ? `Willkommen zurück, ${user.firstName}!`
              : 'Wähle dein Paket'}
          </h1>
        </div>

        {/* Verifizierungs-Banner für eingeloggte Bestands-User ohne bestätigte E-Mail.
            Ohne Verifikation ist kein Abo-Abschluss möglich (Backend blockt). */}
        {showVerificationBanner && (
          <div className="mb-8 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex-shrink-0 hidden sm:block">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800/40 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-amber-700 dark:text-amber-300" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                  E-Mail-Adresse noch nicht bestätigt
                </h2>
                <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                  Bevor du ein Abo abschließen kannst, musst du deine E-Mail-Adresse
                  {user?.email ? <> (<span className="font-medium">{user.email}</span>)</> : ''} bestätigen.
                  Bitte prüfe dein Postfach inkl. Spam-Ordner.
                </p>
              </div>
              <div className="flex-shrink-0">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-100 dark:bg-amber-800/60 hover:bg-amber-200 dark:hover:bg-amber-700/60 px-4 py-2 text-sm font-medium text-amber-900 dark:text-amber-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {resendingVerification ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Erneut senden
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Cards — identisch zur Profil-Ansicht /profil/abonnement */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-brand animate-spin" />
          </div>
        ) : (
          <SubscriptionCards
            plans={plans}
            subscriptions={subscriptionStatus?.subscriptions || []}
            fcmsLimits={fcmsLimits}
            activeDiscount={activeDiscountData}
            onSubscribe={handleSubscribe}
            onCancelSubscription={handleCancelSubscription}
            onReactivateSubscription={handleReactivateSubscription}
            onPlanChange={handlePlanChange}
            onCancelPendingDowngrade={handleCancelPendingDowngrade}
            cancellingSubscription={cancellingSubscription}
            reactivatingSubscription={reactivatingSubscription}
          />
        )}

        {/* Footer: Login-Hinweis (Gast) oder Logout (eingeloggt) + Hinweis */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-200 dark:border-gray-800">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center sm:text-left">
            Du kannst dein Abo jederzeit zum Ende der laufenden Periode kündigen.
            Es gibt keine Mindestlaufzeit.
          </p>
          {isAuthenticated ? (
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Abmelden
            </button>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center sm:text-right">
              Bereits Kunde?{' '}
              <Link to="/login" className="text-brand hover:text-brand-hover font-medium">
                Anmelden
              </Link>
            </p>
          )}
        </div>

        {/* Plan-Wechsel-Dialog (Upgrade Stacker -> Whale o.ae.) */}
        {planChangeDialogOpen && (
          <PlanChangeDialog
            isOpen={planChangeDialogOpen}
            onClose={() => setPlanChangeDialogOpen(false)}
            targetPlanSlug={planChangeTargetSlug}
            onSuccess={handlePlanChangeSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default SubscriptionRequiredPageTW;
